<template>
  <v-container fluid class="pa-0 bg-slate-50 min-h-screen" style="font-family: 'Inter', sans-serif;">
    <div class="p-4">
      <div class="max-w-6xl mx-auto space-y-8">
        
        <!-- HEADER -->
        <div class="mb-6 flex items-center justify-between">
          <div>
            <button @click="$router.back()" class="flex items-center gap-2 text-slate-500 hover:text-orange-600 transition-colors mb-2">
              <v-icon small color="inherit">mdi-arrow-left</v-icon>
              <span class="text-[10px] font-bold uppercase tracking-wider">Voltar</span>
            </button>
            <h1 class="text-2xl font-bold text-slate-700">{{ id ? 'Editar Prefeitura' : 'Nova Prefeitura' }}</h1>
            <p class="text-sm text-slate-500">Cadastre ou atualize as informações da prefeitura e suas configurações de sistema</p>
          </div>
        </div>

        <div class="pb-24">
          <v-form ref="form" v-model="valid" lazy-validation autocomplete="off">
            <div class="space-y-6">
              <!-- SEÇÃO 1: IDENTIFICAÇÃO E LOGO -->
              <div class="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
                <div class="px-6 py-4 flex items-center gap-3" style="background-color: #333333;">
                  <v-icon color="orange" small>mdi-office-building-marker</v-icon>
                  <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">DADOS DA PREFEITURA</h2>
                </div>
                
                <div class="p-8">
                  <v-row>
                    <!-- LOGO UPLOAD -->
                    <v-col cols="12" md="3" class="flex flex-col items-center justify-center border-r border-slate-100 pr-8">
                      <div 
                        class="relative group w-40 h-40 rounded-full border-2 border-dashed border-slate-200 bg-slate-50 flex items-center justify-center cursor-pointer overflow-hidden transition-all hover:border-orange-300"
                        @click="$refs.fileInput.click()"
                      >
                        <img 
                          v-if="data.prefecture_logo" 
                          :src="$store.state.urlImages + data.prefecture_logo" 
                          class="w-full h-full object-contain p-2"
                        >
                        <div v-else class="flex flex-col items-center text-slate-400">
                          <v-icon size="32" color="currentColor">mdi-cloud-upload-outline</v-icon>
                          <span class="text-[9px] font-bold uppercase tracking-widest mt-2">ADICIONAR LOGO</span>
                        </div>
                        
                        <!-- Overlay de Hover -->
                        <div class="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                          <span class="text-white text-[10px] font-bold uppercase tracking-widest">Alterar Imagem</span>
                        </div>
                      </div>
                      <input type="file" ref="fileInput" @change="uploadImage" class="hidden" accept="image/*">
                      <p class="text-[10px] text-slate-400 font-medium mt-3 text-center px-4">Tamanho sugerido: 400x400px (PNG ou JPG)</p>
                    </v-col>

                    <!-- CAMPOS DE IDENTIFICAÇÃO -->
                    <v-col cols="12" md="9" class="pl-8">
                      <v-row dense>
                        <v-col cols="12" md="12">
                           <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1 ml-1">Razão Social</label>
                           <v-text-field
                             v-model="data.prefecture_social_name"
                             placeholder="EX: PREFEITURA MUNICIPAL DE EXEMPLO"
                             solo flat dense hide-details
                             class="custom-input mb-4"
                             :rules="fieldRules"
                           ></v-text-field>
                        </v-col>
                        
                        <v-col cols="12" md="12">
                           <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1 ml-1">Nome Fantasia</label>
                           <v-text-field
                             v-model="data.prefecture_fantasy_name"
                             placeholder="EX: CIDADE EXEMPLO"
                             solo flat dense hide-details
                             class="custom-input mb-4"
                           ></v-text-field>
                        </v-col>

                        <v-col cols="12" md="6">
                           <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1 ml-1">CNPJ</label>
                           <v-text-field
                             v-model="data.prefecture_cnpj"
                             placeholder="00.000.000/0000-00"
                             v-mask-cnpj
                             solo flat dense hide-details
                             class="custom-input"
                             :rules="fieldRules"
                             :error="!validCnpj(data.prefecture_cnpj)"
                           ></v-text-field>
                        </v-col>
                        
                        <v-col cols="12" md="6">
                           <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1 ml-1">Inscrição Estadual</label>
                           <v-text-field
                             v-model="data.prefecture_ie"
                             placeholder="000.000.000"
                             solo flat dense hide-details
                             class="custom-input"
                           ></v-text-field>
                        </v-col>
                      </v-row>
                    </v-col>
                  </v-row>
                </div>
              </div>

              <!-- SEÇÃO 2: CONTATO E FATURAMENTO -->
              <div class="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
                <div class="px-6 py-4 flex items-center gap-3" style="background-color: #333333;">
                  <v-icon color="orange" small>mdi-cash-register</v-icon>
                  <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">CONTATO E FATURAMENTO</h2>
                </div>
                
                <div class="p-8">
                  <v-row dense>
                    <v-col cols="12" md="4">
                       <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1 ml-1">E-mail de Contato</label>
                       <v-text-field
                         v-model="data.prefecture_mail"
                         placeholder="contato@prefeitura.gov.br"
                         solo flat dense hide-details
                         class="custom-input"
                       ></v-text-field>
                    </v-col>
                    
                    <v-col cols="12" md="4">
                       <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1 ml-1">Celular</label>
                       <v-text-field
                         v-model="data.prefecture_cel"
                         v-mask-phone.br
                         placeholder="(00) 00000-0000"
                         solo flat dense hide-details
                         class="custom-input"
                       ></v-text-field>
                    </v-col>

                    <v-col cols="12" md="4">
                       <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1 ml-1">Telefone Fixo</label>
                       <v-text-field
                         v-model="data.prefecture_tel"
                         v-mask-phone.br
                         placeholder="(00) 0000-0000"
                         solo flat dense hide-details
                         class="custom-input"
                       ></v-text-field>
                    </v-col>

                    <v-col cols="12" class="mt-6">
                      <div class="p-6 bg-slate-50 rounded-xl border border-slate-100 border-l-4 border-l-orange-500">
                        <v-row align="center">
                          <v-col cols="12" md="8">
                            <h4 class="text-xs font-black text-slate-700 uppercase tracking-widest mb-1">Configurações de Faturamento</h4>
                            <p class="text-[10px] text-slate-500 font-medium">Defina como o sistema deve processar o faturamento desta prefeitura</p>
                          </v-col>
                          <v-col cols="12" md="4">
                            <v-autocomplete
                              v-model="data.billing_type"
                              :items="['POR VEICULO', 'POR SECRETARIA']"
                              solo flat dense hide-details
                              class="custom-input bg-white"
                            ></v-autocomplete>
                          </v-col>
                        </v-row>
                      </div>
                    </v-col>
                  </v-row>
                </div>
              </div>

              <!-- SEÇÃO 3: ENDEREÇO -->
              <div class="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
                <div class="px-6 py-4 flex items-center gap-3" style="background-color: #333333;">
                  <v-icon color="orange" small>mdi-map-marker-radius</v-icon>
                  <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">ENDEREÇO E LOCALIZAÇÃO</h2>
                </div>
                
                <div class="p-8">
                  <v-row dense>
                    <v-col cols="12" md="3">
                       <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1 ml-1">CEP</label>
                       <div class="relative">
                          <v-text-field
                            v-model="data.prefecture_postal_code"
                            v-mask-cep
                            placeholder="00.000-000"
                            solo flat dense hide-details
                            class="custom-input"
                            @blur="getCep(data.prefecture_postal_code)"
                          ></v-text-field>
                          <v-icon small class="absolute right-3 top-1/2 -translate-y-1/2 text-slate-300">mdi-magnify</v-icon>
                       </div>
                    </v-col>
                    
                    <v-col cols="12" md="7">
                       <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1 ml-1">Logradouro / Rua</label>
                       <v-text-field
                         v-model="data.prefecture_street"
                         placeholder="AVENIDA CENTRAL..."
                         solo flat dense hide-details
                         class="custom-input uppercase"
                       ></v-text-field>
                    </v-col>

                    <v-col cols="12" md="2">
                       <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1 ml-1">Número</label>
                       <v-text-field
                         v-model="data.prefecture_number"
                         placeholder="S/N"
                         solo flat dense hide-details
                         class="custom-input uppercase"
                       ></v-text-field>
                    </v-col>

                    <v-col cols="12" md="3" class="mt-4">
                       <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1 ml-1">Complemento</label>
                       <v-text-field
                         v-model="data.prefecture_complement"
                         placeholder="SALA / BLOCO..."
                         solo flat dense hide-details
                         class="custom-input uppercase"
                       ></v-text-field>
                    </v-col>
                    
                    <v-col cols="12" md="3" class="mt-4">
                       <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1 ml-1">Bairro</label>
                       <v-text-field
                         v-model="data.prefecture_district"
                         placeholder="CENTRO"
                         solo flat dense hide-details
                         class="custom-input uppercase"
                       ></v-text-field>
                    </v-col>

                    <v-col cols="12" md="4" class="mt-4">
                       <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1 ml-1">Cidade</label>
                       <v-text-field
                         v-model="data.prefecture_city"
                         placeholder="NOME DA CIDADE"
                         solo flat dense hide-details
                         class="custom-input uppercase"
                       ></v-text-field>
                    </v-col>

                    <v-col cols="12" md="2" class="mt-4">
                       <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1 ml-1">Estado (UF)</label>
                       <v-text-field
                         v-model="data.prefecture_state"
                         placeholder="UF"
                         solo flat dense hide-details
                         class="custom-input uppercase"
                       ></v-text-field>
                    </v-col>
                  </v-row>
                </div>
              </div>

              <!-- SEÇÃO 4: PARÂMETROS -->
              <div class="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
                <div class="px-6 py-4 flex items-center gap-3" style="background-color: #333333;">
                  <v-icon color="orange" small>mdi-cogs</v-icon>
                  <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">PARÂMETROS</h2>
                </div>
                
                <div class="p-8">
                  <v-row dense>
                    <v-col cols="12">
                      <v-switch
                        v-model="data.block_expired_cnh"
                        color="orange"
                        inset
                        hide-details
                        class="mt-0"
                      >
                        <template v-slot:label>
                          <span class="text-sm font-bold text-slate-700 ml-2">Bloquear abastecimento para motoristas com CNH vencida</span>
                        </template>
                      </v-switch>
                      <p class="text-[10px] text-slate-500 font-medium ml-14 mt-1">Quando ativado, o sistema impedirá o registro de novos abastecimentos se a CNH do motorista estiver fora do prazo de validade.</p>
                    </v-col>
                  </v-row>
                </div>

                <!-- FOOTER INTEGRADO -->
                <div class="bg-slate-50 px-8 py-5 border-t border-slate-200 flex justify-end items-center gap-4">
                  <button 
                    type="button"
                    @click="$router.back()"
                    class="text-slate-500 hover:text-slate-700 font-bold text-[11px] uppercase px-4 py-2 transition-colors flex-none"
                  >
                    Cancelar
                  </button>
                  <div class="flex-none">
                    <button
                      type="button"
                      @click="validate"
                      :disabled="loader || !validCnpj(data.prefecture_cnpj)"
                      class="bg-orange-500 hover:bg-orange-600 text-white font-bold text-[11px] px-8 py-3 rounded shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all w-auto max-w-max whitespace-nowrap flex items-center gap-2"
                    >
                      <v-icon small color="white" class="mr-1" style="font-size: 14px;">mdi-check</v-icon>
                      <span v-if="loader" class="animate-spin h-3 w-3 border-2 border-white border-t-transparent rounded-full"></span>
                      <span v-else>{{ id ? 'SALVAR ALTERAÇÕES' : 'CONFIRMAR CADASTRO' }}</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </v-form>
        </div>

        <!-- NOTIFICAÇÕES -->
        <v-snackbar
          v-model="snack.show"
          :color="snack.color"
          timeout="3000"
          rounded="lg"
          elevation="24"
        >
          <div class="flex items-center gap-3">
            <v-icon color="white" small>
              {{ snack.color === 'success' ? 'mdi-check-circle' : 'mdi-alert-circle' }}
            </v-icon>
            <span class="text-xs font-bold uppercase tracking-wider">{{ snack.message }}</span>
          </div>
        </v-snackbar>
      </div>
    </div>
  </v-container>
</template>

<script>
export default {
  props: ["id"],
  data: () => ({
    valid: false,
    loader: false,
    fieldRules: [(v) => !!v || "Este campo é requerido"],
    snack: {
      show: false,
      message: "",
      color: "success",
    },
    data: {
      prefecture_id: null,
      prefecture_logo: "",
      prefecture_social_name: "",
      prefecture_fantasy_name: "",
      prefecture_cnpj: "",
      prefecture_ie: "",
      prefecture_mail: "",
      prefecture_cel: "",
      prefecture_tel: "",
      prefecture_postal_code: "",
      prefecture_street: "",
      prefecture_number: "",
      prefecture_complement: "",
      prefecture_district: "",
      prefecture_city: "",
      prefecture_state: "",
      billing_type: "POR SECRETARIA",
      block_expired_cnh: false,
    },
  }),
  created() {
    if (this.id) {
      this.$store.dispatch("setLoader", true);
      this.$http.get(`prefectures/${this.id}`).then((r) => {
        this.data = r.data[0];
        delete this.data.created_at;
        this.$store.dispatch("setLoader", false);
      });
    }
  },
  methods: {
    validCnpj(v) {
      if (!v) return false;
      let cnpj = v.replace(/\D/gim, "");
      if (cnpj.length !== 14) return false;
      if (/^(\d)\1+$/.test(cnpj)) return false;
      let t = cnpj.length - 2,
        d = cnpj.substring(t),
        d1 = parseInt(d.charAt(0)),
        d2 = parseInt(d.charAt(1)),
        calc = (x) => {
          let n = cnpj.substring(0, x), y = x - 7, s = 0;
          for (let i = x; i >= 1; i--) {
            s += n.charAt(x - i) * y--;
            if (y < 2) y = 9;
          }
          let r = 11 - (s % 11);
          return r > 9 ? 0 : r;
        };
      return calc(t) === d1 && calc(t + 1) === d2;
    },
    validate() {
      if (this.$refs.form.validate() && this.validCnpj(this.data.prefecture_cnpj)) {
        this.createOrUpdate();
      } else {
        this.snack.show = true;
        this.snack.color = "warning";
        this.snack.message = "Atencão: Verifique os campos obrigatórios e o CNPJ!";
      }
    },
    createOrUpdate() {
      this.loader = true;
      const method = this.id ? 'put' : 'post';
      const endpoint = this.id ? `/prefectures/${this.id}` : "/prefectures";
      
      this.$store.dispatch("setLoader", true);
      this.$http[method](endpoint, this.data)
        .then((r) => {
          this.snack.show = true;
          this.snack.message = r.data.result;
          this.snack.color = "success";
          setTimeout(() => {
            this.$router.push("/prefectures-manager");
          }, 1500);
        })
        .catch((e) => {
          this.snack.show = true;
          this.snack.message = e.message;
          this.snack.color = "error";
          this.loader = false;
          this.$store.dispatch("setLoader", false);
        });
    },
    uploadImage(event) {
      this.$store.dispatch("setLoader", true);
      const fd = new FormData();
      fd.append("image", event.target.files[0], event.target.files[0].name);
      this.$http
        .post("/upload", fd)
        .then((r) => {
          this.data.prefecture_logo = r.data.cover;
          this.$store.dispatch("setLoader", false);
        })
        .catch((e) => {
          alert("Erro ao realizar upload da imagem tente novamente! " + e.message);
          this.$store.dispatch("setLoader", false);
          event.target.files = [];
        });
    },
    getCep(cep) {
      if (cep && cep.length == 10) {
        this.$store.dispatch("setLoader", true);
        const cleanCep = cep.replace(/\D/g, "");
        this.$http
          .get("https://viacep.com.br/ws/" + cleanCep + "/json/")
          .then((response) => {
            this.data.prefecture_street = response.data.logradouro;
            this.data.prefecture_district = response.data.bairro;
            this.data.prefecture_city = response.data.localidade;
            this.data.prefecture_state = response.data.uf;
            this.$store.dispatch("setLoader", false);
          })
          .catch(() => {
            this.snack.show = true;
            this.snack.color = "warning";
            this.snack.message = "CEP não encontrado!";
            this.$store.dispatch("setLoader", false);
          });
      }
    },
  },
};
</script>

<style scoped>
.min-h-screen { min-height: 100vh; }
.bg-slate-50 { background-color: #f8fafc; }
.border-slate-200 { border-color: #e2e8f0; }

.custom-input ::v-deep .v-input__control { min-height: 44px !important; }
.custom-input ::v-deep .v-input__slot { 
  background-color: #f8fafc !important; 
  border: 1px solid #e2e8f0 !important;
  box-shadow: none !important;
  border-radius: 0.6rem !important;
  padding: 0 14px !important;
  transition: all 0.2s ease;
}
.custom-input.v-input--is-focused ::v-deep .v-input__slot {
  border-color: #f97316 !important;
  background-color: #fff !important;
  box-shadow: 0 0 0 1px #f97316 !important;
}
.custom-input ::v-deep input {
    font-size: 0.85rem !important;
    font-weight: 700 !important;
    color: #334155 !important;
    text-transform: uppercase !important;
}
.custom-input ::v-deep input::placeholder {
    color: #cbd5e1 !important;
    font-weight: 500 !important;
    text-transform: none !important;
}

/* Chrome, Safari, Edge, Opera */
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.shadow-lg { box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05); }
</style>
